import { useClientPortalAPI } from "@certn/api";
import { Stack } from "@certn/cascadia";
import { Card } from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { PackageSummaryCard } from "../../components/package-summary-card/PackageSummaryCard";
import { PageBanner } from "../../components/page-banner/PageBanner";
import { useUserFeatures } from "../../components/user-feature/UserFeatureProvider";
import { Schemas } from "../../types";

export function PersonalisedPackages() {
  const { t } = useTranslation();
  const { updateFeature } = useUserFeatures();
  const { data, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/ssb/personalised-packages/"
  );

  const sortedPackages = (data?.results ?? [])
    .filter((pkg) => pkg?.price.price > 0)
    .sort((a, b) => {
      const aChecksLength = Array.isArray(a.checks) ? a.checks.length : 1;
      const bChecksLength = Array.isArray(b.checks) ? b.checks.length : 1;
      return aChecksLength - bChecksLength;
    });

  if (isLoading) {
    return (
      <Card className={styles.container}>
        <Stack gap="space-2" orientation="horizontal">
          <Stack gap="space-2" className={styles.flexOne}>
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack gap="space-2" className={styles.flexOne}>
            <Skeleton />
            <Skeleton />
          </Stack>
          <Stack gap="space-2" className={styles.flexOne}>
            <Skeleton />
            <Skeleton />
          </Stack>
        </Stack>
      </Card>
    );
  }

  if (sortedPackages?.length === 0) {
    return null;
  }

  return (
    <PageBanner
      title={t("Personalised packages created for you")}
      onClose={() => updateFeature("PERSONALIZED_PACKAGES", false)}
    >
      {sortedPackages?.map((packageData) => (
        <PackageSummaryCard
          id={packageData.id}
          key={packageData.id}
          title={packageData.name}
          price={packageData.price.price}
          currency={packageData.price.currency as Schemas["CurrencyEnum"]}
          checks={[...packageData.checks]}
          testID={`personalised-package-${packageData.id}`}
        />
      ))}
    </PageBanner>
  );
}

const styles = {
  container: css({
    paddingRight: "var(--space-5)",
    paddingLeft: "var(--space-5)",
    paddingTop: "var(--space-6)",
    paddingBottom: "var(--space-6)",
  }),
  flexOne: css({
    flex: 1,
  }),
};
